/**
 * PII scrubbing. Applied to every string field before insert. Zero-PII
 * is a hard requirement per Alli's policy — even if a caller accidentally
 * interpolated a user's email into an error message, it gets redacted
 * before it lands.
 */

const EMAIL = /[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi
// Phone: require an explicit phone indicator (+, parens, or NANP hyphen
// pattern) so we don't false-positive dates like "2026-09-15" or IDs.
const PHONE = /(?:\+\d[\d\s\-()]{6,15}\d|\(\d{3}\)[\s\-.]?\d{3}[\s\-.]?\d{4}|\b\d{3}[-.]\d{3}[-.]\d{4}\b)/g
// SIN: require at least one separator so we don't strip 9-digit IDs
// or process IDs. Canadian format is XXX-XXX-XXX or XXX XXX XXX.
const SIN = /\b\d{3}[- ]\d{3}[- ]\d{3}\b/g
const CREDIT_CARD = /\b(?:\d[ -]*?){13,19}\b/g

export function scrubString(input: string, opts?: { keepUuids?: boolean }): string {
  let out = input.replace(EMAIL, '<email>').replace(PHONE, '<phone>').replace(SIN, '<sin>').replace(CREDIT_CARD, '<cc>')
  if (!opts?.keepUuids) {
    out = out.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<uuid>')
  }
  return out
}

const SENSITIVE_KEYS = new Set([
  'password',
  'currentpassword',
  'newpassword',
  'token',
  'accesstoken',
  'refreshtoken',
  'secret',
  'apikey',
  'authorization',
  'cookie',
  'ssn',
  'sin',
  'email',
  'phone',
  'address',
  'dob',
  'dateofbirth',
])

/**
 * Recursively scrub an object. Strips values under sensitive keys entirely
 * and applies string-level scrubbing to remaining string values.
 * Returns a deep copy — never mutates input.
 */
export function scrubObject(input: unknown, depth = 0): unknown {
  if (depth > 8) return '<truncated>'
  if (input == null) return input
  if (typeof input === 'string') return scrubString(input)
  if (typeof input === 'number' || typeof input === 'boolean') return input
  if (Array.isArray(input)) return input.map((v) => scrubObject(v, depth + 1))
  if (typeof input === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.has(key.toLowerCase())) continue
      out[key] = scrubObject(value, depth + 1)
    }
    return out
  }
  return String(input)
}

/**
 * Fingerprint normalizer — strips variable parts (numbers, uuids, quoted
 * values) AND PII patterns so groups collapse properly. Called by the
 * fingerprint computer in `insert.ts`.
 */
export function normalizeForFingerprint(input: string): string {
  return input
    .replace(EMAIL, '<email>')
    .replace(PHONE, '<phone>')
    .replace(SIN, '<sin>')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<uuid>')
    .replace(/["'`][^"'`\n]*["'`]/g, '"?"')
    // No word-boundary — `\b\d+\b` fails on things like "(51ms)" because
    // both `1` and `m` are word chars. We want aggressive digit collapse
    // for fingerprint grouping: `(51ms)` and `(52ms)` must yield the same
    // key so the "Long task detected (Nms)" crashes collapse to one row.
    .replace(/\d+/g, '<n>')
    .trim()
    .slice(0, 200)
}
